/**
 * World 2 — "Redline" (132 BPM, amber/violet). Faster song = literally faster
 * levels (spec 5.2); same jump-in-rows math thanks to D-010.
 */
import { buildLevel, every, rep, F, GAP } from "./build";
import type { LevelFile } from "../track/types";

/** ~63 s. W2 opener: denser singles + gaps at speed. 2 pads, 2 checkpoints. */
export const W2_L1: LevelFile = buildLevel({
  id: "w2-l1",
  name: "Ignition",
  songId: "redline",
  world: 2,
  rows: [
    ...rep(F, 16),
    ...every(4, "___1___", "__1____", "____1__", "_1___1_", "__1_1__", "_11_11_"),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    ...every(4, "_11____", "____11_", "_11____", "____11_", "__2_2__"),
    ...rep(F, 4),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "XX___XX", "X__X__X", "_X_X_X_", "XXX_XXX"),
    "___o___",
    ...rep(F, 5),
    ...rep("..TTT..", 8),
    ...rep(F, 6),
    "___B___",
    ...rep(F, 11),
    ...every(4, "11___11", "111_111", "_11_11_", "11_1_11"),
    GAP,
    ...rep(F, 3),
    // dual-ribbon choice: a block splits the road, both sides continue with
    // their own hazards — pick a line and commit
    "___1___",
    "___.___",
    "_1_.___",
    "___.___",
    "___._1_",
    ...rep("___.___", 2),
    F,
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "__2____", "____2__", "_2___2_", "2_____2"),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "______o",
    ...rep(F, 7),
    ...every(4, "1_1_1_1", "_1_1_1_"),
    ...rep(F, 12),
    GAP,
    ...rep(F, 3),
    ...rep(F, 27),
    "FFFFFFF",
  ],
});

/** ~68 s. Hazard walls + long tunnels. 3 pads, 2 checkpoints. */
export const W2_L2: LevelFile = buildLevel({
  id: "w2-l2",
  name: "Afterburn",
  songId: "redline",
  world: 2,
  rows: [
    ...rep(F, 12),
    ...every(4, "_1___1_", "__2_2__", "1__1__1", "_2___2_"),
    ...rep(F, 4),
    ...every(4, "XXX_XXX", "XX_X_XX", "X_____X", "_XX_XX_"),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    ...rep("..TTT..", 10),
    ...rep(F, 6),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "_11_11_", "11___11", "1_1_1_1"),
    "XXXXXXX",
    ...rep(F, 3),
    "___o___",
    ...rep(F, 5),
    "___B___",
    ...rep(F, 11),
    ...rep(".TTTTT.", 8),
    ...rep(F, 4),
    ...every(4, "X__X__X", "XX___XX", "XXX_XXX", "_X_X_X_"),
    "CCCCCCC",
    ...rep(F, 7),
    GAP,
    ...rep(F, 3),
    "__2_2__",
    ...rep(F, 3),
    // zigzag isles at 132 BPM: hop lane 2 → lane 4 → back, one per bar
    "..___..",
    "..__...",
    ...rep(".._....", 3),
    ...rep("...._..", 3),
    ...rep(".._....", 2),
    "..___..",
    F,
    ...rep(F, 4),
    "______o",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    "XXXXXXX",
    ...rep(F, 3),
    "XXXXXXX",
    ...rep(F, 3),
    ...every(4, "111_111", "1__1__1"),
    ...rep(F, 12),
    GAP,
    ...rep(F, 3),
    ...rep(F, 21),
    "FFFFFFF",
  ],
});

/** ~75 s. Narrows, sticky traps, height-3 monoliths. 3 pads, 3 checkpoints. */
export const W2_L3: LevelFile = buildLevel({
  id: "w2-l3",
  name: "Meltdown",
  songId: "redline",
  world: 2,
  rows: [
    ...rep(F, 12),
    ...every(4, "__1_1__", "_2___2_", "1__2__1", "_11_11_"),
    "___S___",
    ...rep(F, 7),
    ...every(4, "__1____", "____1__", "_1___1_"),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    ...rep("..___..", 10),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "__3_3__", "_3___3_", "3__3__3"),
    "__S_S__",
    ...rep(F, 7),
    ...every(4, "XX_X_XX", "X_XXX_X", "XXX_XXX"),
    "___o___",
    ...rep(F, 5),
    "___B___",
    ...rep(F, 11),
    ...rep("..TTT..", 8),
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    // hazard bridge: a single lane, barriers ON it — hop without drifting
    ...rep("..._...", 2),
    "...X...",
    ...rep("..._...", 2),
    "...X...",
    ...rep("..._...", 2),
    ...rep(F, 4),
    GAP,
    ...rep(F, 3),
    "_2___2_",
    ...rep(F, 3),
    ...every(4, "11_1_11", "111_111", "1_111_1"),
    "CCCCCCC",
    ...rep(F, 7),
    "______o",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    "XXXXXXX",
    ...rep(F, 3),
    ...every(4, "_3_3_3_", "3_3_3_3"),
    GAP,
    ...rep(F, 3),
    ...rep(F, 10),
    GAP,
    ...rep(F, 3),
    ...rep(F, 25),
    "FFFFFFF",
  ],
});

/** ~82 s. The gauntlet: everything at 132 BPM, boosted + raw double gaps.
 *  5 pads, 3 checkpoints. */
export const W2_L4: LevelFile = buildLevel({
  id: "w2-l4",
  name: "Terminal Velocity",
  songId: "redline",
  world: 2,
  rows: [
    ...rep(F, 12),
    ...every(4, "_2_1_2_", "__2_2__", "2__1__2", "_1_2_1_"),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    "_o_____",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 11),
    ...every(4, "XXX_XXX", "XX_X_XX", "X_XXX_X", "_XX_XX_"),
    ...rep("..TTT..", 8),
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    "__S_S__",
    ...rep(F, 7),
    ...every(4, "_2___2_", "2__2__2", "__3_3__"),
    "___B___",
    ...rep(F, 11),
    GAP,
    ...rep(F, 3),
    GAP,
    ...rep(F, 3),
    // gauntlet zigzag: Δ2 island hops with a full void row mid-chain
    "..___..",
    "..__...",
    ...rep(".._....", 3),
    ...rep("...._..", 3),
    GAP,
    ...rep(".._....", 3),
    "..___..",
    "._____.",
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "111_111", "11_1_11", "1_111_1", "_111_1_"),
    "___o___",
    ...rep(F, 5),
    "___B___",
    ...rep(F, 11),
    "XXXXXXX",
    ...rep(F, 3),
    "XXXXXXX",
    ...rep(F, 3),
    ...rep(".TTTTT.", 6),
    ...rep(F, 4),
    "CCCCCCC",
    ...rep(F, 7),
    ...every(4, "_3_3_3_", "3__3__3", "__3_3__"),
    "______o",
    ...rep(F, 7),
    "___B___",
    ...rep(F, 5),
    ...rep(F, 2),
    GAP,
    GAP, // boosted double gap
    ...rep(F, 6),
    GAP,
    GAP, // raw double gap — the hardest jump in the game
    ...rep(F, 10),
    "___B___",
    ...rep(F, 11),
    ...every(4, "1_1_1_1", "_1_1_1_"),
    ...rep(F, 20),
    "FFFFFFF",
  ],
});
